"use client";

import { PricingTier } from "@/components/pricing-tier";
import { trackEvent } from "@/components/vident-provider";
import { authClient } from "@/lib/auth-client";
import { usePlan } from "@/lib/use-plan";
import { TIER_ORDER, type UserTier } from "@unranked/shared";

type TierConfig = {
	name: string;
	slug: UserTier;
	price: string;
	period: string;
	description: string;
	features: string[];
	highlighted?: boolean;
	delay: string;
};

export function PricingTiersDynamic({ tiers }: { tiers: TierConfig[] }) {
	const { data: session, isPending: sessionPending } = authClient.useSession();
	const { plan, loading: planLoading } = usePlan();

	const resolving = sessionPending || (!!session && planLoading);
	const isLoggedIn = !!session;
	const currentTier = plan?.tier ?? null;

	function getCtaAndAction(slug: UserTier) {
		if (!isLoggedIn) {
			return { cta: "Get Started", href: `/sign-up?plan=${slug}`, onSelect: undefined };
		}

		if (!currentTier) {
			return {
				cta: "Get Started",
				href: "#",
				onSelect: () => {
					trackEvent("upgrade_initiated", { plan: slug });
					authClient.checkout({ slug, successUrl: "/dashboard?upgraded=true" });
				},
			};
		}

		const currentIdx = TIER_ORDER.indexOf(currentTier);
		const targetIdx = TIER_ORDER.indexOf(slug);

		if (targetIdx === currentIdx) {
			return { cta: "Current Plan", href: "#", onSelect: undefined };
		}

		if (targetIdx > currentIdx) {
			return {
				cta: "Upgrade",
				href: "#",
				onSelect: () => {
					trackEvent("upgrade_initiated", { plan: slug });
					authClient.checkout({ slug, successUrl: "/dashboard?upgraded=true" });
				},
			};
		}

		return {
			cta: "Downgrade",
			href: "#",
			onSelect: () => authClient.customer.portal(),
		};
	}

	return (
		<div className="grid gap-6 md:grid-cols-3">
			{tiers.map((tier) => {
				const isCurrent = !resolving && isLoggedIn && currentTier === tier.slug;
				const isUpgrade =
					!resolving &&
					isLoggedIn &&
					currentTier != null &&
					TIER_ORDER.indexOf(tier.slug) > TIER_ORDER.indexOf(currentTier);
				const isDowngrade =
					!resolving &&
					isLoggedIn &&
					currentTier != null &&
					TIER_ORDER.indexOf(tier.slug) < TIER_ORDER.indexOf(currentTier);
				const { cta, href, onSelect } = resolving
					? { cta: "Get Started", href: `/sign-up?plan=${tier.slug}`, onSelect: undefined }
					: getCtaAndAction(tier.slug);

				return (
					<PricingTier
						key={tier.slug}
						name={tier.name}
						price={tier.price}
						period={tier.period}
						description={tier.description}
						features={tier.features}
						highlighted={tier.highlighted}
						delay={tier.delay}
						cta={cta}
						href={href}
						isCurrent={isCurrent}
						isUpgrade={isUpgrade}
						isDowngrade={isDowngrade}
						onSelect={onSelect}
					/>
				);
			})}
		</div>
	);
}
